import { Box } from '@mui/material';
import React, { useRef, useState } from 'react';
import STITCH_TYPES from './stitchTypes';
import StitchSymbol from './StitchSymbol';
import { renderIconSvg } from './iconsSvg';

const ChartGrid = ({ 
  knitdata, // main stitch data (required)
  colordata, // color indices (array of indices)
  colors, // color palette (array of color strings)
  icons, // icon palette (array of icon keys, indexed by color index)
  onStitchClick,
  onClearCell,
  cellSize = 16,
  startWithRS = true,
  deleteMode = false,
  verticalLines = [], // array of column indices (0-based)
  onToggleVerticalLine, // function(colIdx)
  selectedStitch, // NEW: stitch key selected in editor
  mode // NEW: editor mode
}) => {
  // Create a reversed copy of the grid for bottom-up display
  const displayGrid = [...knitdata].reverse();
  // Calculate the maximum number of columns in the grid
  const maxCols = knitdata[0] ? knitdata[0].length : 0;
  
  // Generate column headers (1-based index)
  // For each row, column numbers should be reversed if the row is RS
  // We'll need to pass the row index to the column header rendering

  // Helper to get column numbers for a given row
  const getColumnNumbers = () => {
    const cols = Array.from({ length: maxCols }, (_, i) => i + 1);
    return startWithRS ? [...cols].reverse() : cols;
  };

  // Track mouse down for drag-to-paint
  const [isMouseDown, setIsMouseDown] = useState(false);
  const mouseButtonRef = useRef(null); // Track which button is pressed
  // Track hovered cell for shading
  const [hoveredCell, setHoveredCell] = useState(null); // {row, col} or null

  // Helper to handle mouse up globally
  React.useEffect(() => {
    const handleMouseUp = () => {
      setIsMouseDown(false);
      mouseButtonRef.current = null;
    };
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, []);

  // Compute which cells should be shaded on hover (for stitch placement)
  let hoverCells = [];
  if (
    mode === 'stitch' &&
    hoveredCell &&
    selectedStitch &&
    STITCH_TYPES[selectedStitch]
  ) {
    const { row, col } = hoveredCell;
    const width = STITCH_TYPES[selectedStitch].width || 1;
    for (let i = 0; i < width; i++) {
      hoverCells.push(`${row}-${col + i}`);
    }
  }

  // Render a single cell, using all available layers
  const renderCell = (row, col) => {
    const stitchType = knitdata[row][col];
    // Get color index for this cell
    const colorIdx = colordata && colordata[row] ? colordata[row][col] : undefined;
    const color = (typeof colorIdx === 'number' && colors && colors[colorIdx]) ? colors[colorIdx] : undefined;
    // Use color index to look up icon in palette
    let icon = '';
    if (icons && typeof colorIdx === 'number') {
      icon = icons[colorIdx] || '';
    }
    const isRS = startWithRS ? (row % 2 === 0) : (row % 2 !== 0);
    const isPartOfMultiCell = stitchType === null;
    const stitchWidth = STITCH_TYPES[stitchType]?.width || 1;

    // Skip rendering if this cell is part of a multi-cell stitch but not the main cell
    if (isPartOfMultiCell) return null;

    // Only show top border for last row (top visually), and left border for first column
    const showTop = row === knitdata.length - 1;
    const showLeft = col === 0;
    const totalWidth = cellSize * stitchWidth;

    // Check if a vertical line should be drawn after this cell
    const showVerticalLine = verticalLines.includes(col + stitchWidth - 1);
    // Check if a vertical line should be drawn on the left edge
    const showLeftVerticalLine = col === 0 && verticalLines.includes(-1);
    // Check if a vertical line should be drawn on the right edge (after last column)
    const showRightVerticalLine = (col + stitchWidth - 1 === maxCols - 1) && verticalLines.includes(maxCols - 1);

    // Is this cell hovered for stitch placement?
    const isHoverShaded = hoverCells.includes(`${row}-${col}`);

    return (
      <Box
        key={`${row}-${col}`}
        onMouseDown={e => {
          setIsMouseDown(true);
          mouseButtonRef.current = e.button;
          if (e.button === 0) {
            onStitchClick(row, col);
          } else if (e.button === 2 && onClearCell) {
            onClearCell(row, col, e);
          }
        }}
        onMouseEnter={e => {
          if (isMouseDown) {
            if (mouseButtonRef.current === 0) {
              onStitchClick(row, col);
            } else if (mouseButtonRef.current === 2 && onClearCell) {
              onClearCell(row, col, e);
            }
          }
          // Track hover for shading
          if (mode === 'stitch' && selectedStitch) {
            setHoveredCell({ row, col });
          }
        }}
        onMouseLeave={() => {
          // Only clear hover if this is the hovered cell
          if (hoveredCell && hoveredCell.row === row && hoveredCell.col === col) {
            setHoveredCell(null);
          }
        }}
        onContextMenu={e => {
          e.preventDefault();
          if (!isMouseDown && onClearCell) onClearCell(row, col, e);
        }}
        sx={{
          width: totalWidth,
          height: cellSize,
          borderTop: showTop ? '1px solid #ddd' : 'none',
          borderLeft: showLeftVerticalLine ? '3px solid #1976d2' : (showLeft ? '1px solid #ddd' : undefined),
          borderBottom: '1px solid #ddd',
          borderRight: showVerticalLine ? '3px solid #1976d2' : showRightVerticalLine ? '3px solid #1976d2' : '1px solid #ddd',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          cursor: deleteMode ? 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'red\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Ccircle cx=\'12\' cy=\'12\' r=\'10\'/%3E%3Cline x1=\'8\' y1=\'8\' x2=\'16\' y2=\'16\'/%3E%3Cline x1=\'16\' y1=\'8\' x2=\'8\' y2=\'16\'/%3E%3C/svg%3E") 8 8, not-allowed' : 'pointer',
          backgroundColor: isHoverShaded
            ? 'rgba(25, 118, 210, 0.18)'
            : (color || 'white'),
          '&:hover': {
            backgroundColor: isHoverShaded
              ? 'rgba(25, 118, 210, 0.22)'
              : (deleteMode ? '#ffebee' : (color ? color : '#f5f5f5')),
          },
        }}
      >
        {/* Render icon if set, above color background, below stitch symbol */}
        {icon && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <svg width={cellSize} height={cellSize} viewBox="0 0 10 10" style={{ display: 'block' }}>
              {renderIconSvg(icon, color, false)}
            </svg>
          </Box>
        )}
        {/* Render stitch symbol above icon */}
        {stitchType && stitchType !== 'empty' && (
          <StitchSymbol 
            type={stitchType} 
            isRS={isRS} 
            cellSize={cellSize} 
          />
        )}
      </Box>
    );
  };

  // Render vertical line hotspots (as a visible row above the grid, aligned with columns)
  const renderVerticalLineHotspots = () => {
    if (!onToggleVerticalLine) return null;
    if (maxCols < 1) return null;
    // Constants for alignment
    const leftMargin = 20; // px, matches row number width
    const hotspotWidth = 16; // px, wider for easier clicking
    const containerWidth = leftMargin * 2 + cellSize * maxCols;
    return (
      <Box sx={{
        position: 'relative',
        width: `${containerWidth}px`,
        height: 28, // increased from 24 for more space
        mb: '-4px',
        zIndex: 2,
        userSelect: 'none',
        pt: '2px', // add a little padding on top
        pb: '2px', // add a little padding on bottom
      }}>
        {/* Left edge hotspot */}
        <Box
          key={`vline-hotspot-left`}
          onClick={() => onToggleVerticalLine(-1)}
          sx={{
            position: 'absolute',
            left: `${leftMargin - hotspotWidth / 2}px`,
            top: 0,
            width: `${hotspotWidth}px`,
            height: '100%',
            cursor: 'pointer',
            zIndex: 2,
            background: verticalLines.includes(-1) ? 'rgba(25,118,210,0.08)' : 'transparent',
            '&:hover': {
              background: 'rgba(25,118,210,0.15)',
              // Remove boxShadow to prevent cutoff
            },
            borderRadius: 2,
            transition: 'background 0.15s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title={verticalLines.includes(-1) ? 'Remove left edge vertical line' : 'Add left edge vertical line'}
        >
          <Box sx={{ width: 2, height: 16, bgcolor: verticalLines.includes(-1) ? 'primary.main' : '#bbb', opacity: 0.3, borderRadius: 1 }} />
        </Box>
        {/* Between columns hotspots */}
        {Array.from({ length: maxCols - 1 }).map((_, colIdx) => {
          // Center hotspot between columns
          const left = leftMargin + cellSize * (colIdx + 1) - hotspotWidth / 2;
          return (
            <Box
              key={`vline-hotspot-${colIdx}`}
              onClick={() => onToggleVerticalLine(colIdx)}
              sx={{
                position: 'absolute',
                left: `${left}px`,
                top: 0,
                width: `${hotspotWidth}px`,
                height: '100%',
                cursor: 'pointer',
                zIndex: 2,
                background: verticalLines.includes(colIdx) ? 'rgba(25,118,210,0.08)' : 'transparent',
                '&:hover': {
                  background: 'rgba(25,118,210,0.15)',
                  // Remove boxShadow to prevent cutoff
                },
                borderRadius: 2,
                transition: 'background 0.15s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title={verticalLines.includes(colIdx) ? 'Remove vertical line' : 'Add vertical line'}
            >
              {/* Faint vertical bar for visual cue */}
              <Box sx={{ width: 2, height: 16, bgcolor: verticalLines.includes(colIdx) ? 'primary.main' : '#bbb', opacity: 0.3, borderRadius: 1 }} />
            </Box>
          );
        })}
        {/* Right edge hotspot */}
        <Box
          key={`vline-hotspot-right`}
          onClick={() => onToggleVerticalLine(maxCols - 1)}
          sx={{
            position: 'absolute',
            left: `${leftMargin + cellSize * maxCols - hotspotWidth / 2}px`,
            top: 0,
            width: `${hotspotWidth}px`,
            height: '100%',
            cursor: 'pointer',
            zIndex: 2,
            background: verticalLines.includes(maxCols - 1) ? 'rgba(25,118,210,0.08)' : 'transparent',
            '&:hover': {
              background: 'rgba(25,118,210,0.15)',
              // Remove boxShadow to prevent cutoff
            },
            borderRadius: 2,
            transition: 'background 0.15s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title={verticalLines.includes(maxCols - 1) ? 'Remove right edge vertical line' : 'Add right edge vertical line'}
        >
          <Box sx={{ width: 2, height: 16, bgcolor: verticalLines.includes(maxCols - 1) ? 'primary.main' : '#bbb', opacity: 0.3, borderRadius: 1 }} />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '4px',
      overflow: 'auto',
      maxWidth: '100%',
      position: 'relative'
    }}>
      {/* Render vertical line hotspots as a visible row above the grid */}
      {renderVerticalLineHotspots()}
      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
        {/* Top column numbers (use top row's RS/WS for direction) */}
        {false && <Box sx={{ display: 'flex' }}>
          <Box sx={{ width: '20px', flexShrink: 0 }} />
          {(() => {
            const topRowIdx = 0;
            const isRS = startWithRS ? (topRowIdx % 2 === 0) : (topRowIdx % 2 !== 0);
            return getColumnNumbers(topRowIdx, isRS).map((colNum) => (
              <Box 
                key={`col-top-${colNum}`}
                sx={{
                  width: cellSize,
                  height: cellSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.6rem',
                  color: '#495057',
                  flexShrink: 0
                }}
              >
                {colNum}
              </Box>
            ));
          })()}
          <Box sx={{ width: '20px', flexShrink: 0 }} />
        </Box>}

        <Box sx={{ display: 'flex', flex: 1 }}>
          {/* Remove separate left row numbers column */}
          <Box>
            {displayGrid.map((row, displayIndex) => {
              const originalIndex = knitdata.length - 1 - displayIndex;
              const isRS = startWithRS ? (originalIndex % 2 === 0) : (originalIndex % 2 === 1);
              
              return (
                <Box 
                  key={`row-${originalIndex}`}
                  sx={{ 
                    display: 'flex',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.01)'
                    }
                  }}
                >
                  {/* Left row number, now inside the row */}
                  <Box sx={{
                    width: '20px',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.6rem',
                    color: '#495057',
                    userSelect: 'none', // Prevent text selection
                  }}>
                    {isRS ? '' : originalIndex + 1}
                  </Box>
                  {/* Grid cells */}
                  {row.map((_, colIndex) => renderCell(originalIndex, colIndex))}
                  {/* Right side row number */}
                  <Box sx={{
                    width: '20px',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.6rem',
                    color: '#495057',
                    userSelect: 'none', // Prevent text selection
                  }}>
                    {isRS ? originalIndex + 1 : ''}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Bottom column numbers (use bottom row's RS/WS for direction) */}
        <Box sx={{ 
          display: 'flex',
          userSelect: 'none', // Prevent text selection while dragging
        }}>
          <Box sx={{ width: '20px', flexShrink: 0, userSelect: 'none' }} />
          {(() => {
            const bottomRowIdx = knitdata.length - 1;
            const isRS = startWithRS ? (bottomRowIdx % 2 === 0) : (bottomRowIdx % 2 !== 0);
            return getColumnNumbers(bottomRowIdx, isRS).map((colNum) => (
              <Box 
                key={`col-bottom-${colNum}`}
                sx={{
                  width: cellSize,
                  height: cellSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.6rem',
                  color: '#495057',
                  flexShrink: 0,
                  userSelect: 'none', // Prevent text selection
                }}
              >
                {colNum}
              </Box>
            ));
          })()}
          <Box sx={{ width: '20px', flexShrink: 0, userSelect: 'none' }} />
        </Box>
      </Box>
    </Box>
  );
};

export default ChartGrid;
